import { Component, inject, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Clipboard } from '@capacitor/clipboard';
import {
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  MenuController,
} from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';

import { USER_MENU_ID } from '@ninety/mobile/platform/api';
import { environment } from '@ninety/mobile/platform/environments';
import {
  APP_ROUTES,
  CurrentUserSelectors,
  NavMenuIntercomActions,
  SessionActions,
} from '@ninety/mobile/platform/state';

import { RippleComponent } from '../gestures/ripple.component';
import { SideMenuToolbarComponent } from '../side-menu-toolbar/side-menu-toolbar.component';

@Component({
  selector: 'nty-user-menu',
  imports: [
    LetDirective,
    RouterModule,
    SideMenuToolbarComponent,
    RippleComponent,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
  ],
  host: { class: 'nty-side-menu' },
  template: `
    <ion-menu [menuId]="userMenuId" [contentId]="contentId" *ngrxLet="vm$ as vm">
      <ion-header>
        <nty-side-menu-toolbar mode="user" />
      </ion-header>
      <ion-content>
        <div class="user-role-and-email terra-body">
          <nty-ripple [time]="100" (press)="copyEmail(vm.user?.primaryEmail)" />
          <p>{{ vm.user?.title || vm.user?.roleName }}</p>
          <p class="user-email">{{ vm.user?.primaryEmail }}</p>
        </div>

        <ion-list id="user-menu-options-list">
          @if (!vm.user?.isObserver) {
            <ion-menu-toggle>
              <ion-item detail="false" lines="none" [routerLink]="settingsUrl" (click)="close()">
                <ion-icon slot="start" src="assets/icons/settings--regular.svg" />
                <ion-label><span class="terra-body">Settings</span></ion-label>
              </ion-item>
            </ion-menu-toggle>
          }

          <ion-item button="true" detail="false" lines="none" (click)="launchIntercom()">
            <ion-icon slot="start" src="assets/icons/question-circle--regular.svg" />
            <ion-label><span class="terra-body">Help & Support</span></ion-label>
          </ion-item>

          <ion-item button="true" detail="false" lines="none" (click)="logOut()">
            <ion-icon slot="start" src="assets/icons/sign-out--regular.svg" />
            <ion-label><span class="terra-body">Sign out</span></ion-label>
          </ion-item>
        </ion-list>
      </ion-content>
      <div class="version-container">
        <hr />
        <div class="version-info">
          <span class="version-text">Version: {{ appVersion }}</span>
        </div>
      </div>
    </ion-menu>
  `,
  styles: `
    @use 'terra';

    .user-role-and-email {
      text-align: center;
      color: terra.color(text);

      * {
        margin: 0;
      }
    }

    .divider {
      background: terra.color(border);
    }

    .user-email {
      color: terra.color(text, light);
    }

    ion-menu {
      --background: #{terra.color(background, heavy)};
    }

    ion-list {
      margin-top: 24px;
      background: terra.color(background, heavy);

      ion-icon {
        color: terra.color(text, lightest);
      }
    }

    ion-item {
      --padding-top: 4px;
      --padding-end: 3px;
      --padding-bottom: 4px;
      --padding-start: 3px;
      --border-radius: #{terra.radius(small)};
      --background: #{terra.color(background, heavy)};
    }

    ion-content {
      --padding-top: 8px;
      --padding-start: 24px;
      --padding-end: 24px;
      --background: #{terra.color(background, heavy)};
    }

    ion-menu::part(container) {
      @include terra.elevation(2, right);
    }

    ion-label {
      color: terra.color(text) !important;
    }

    hr {
      background: terra.color(border);
      margin: 8px 24px 0;
    }

    .version {
      &-container {
        width: 100%;
        display: flex;
        flex-direction: column;
      }
    }

    .version-info {
      width: 100%;
      text-align: center;
      padding: 16px;
      color: terra.color(text, light);
      @include terra.text(body);
    }
  `,
})
export class UserMenuComponent {
  @Input({ required: true }) contentId!: string;

  private readonly menuCtrl = inject(MenuController);
  private readonly store = inject(Store);

  public readonly userMenuId = inject(USER_MENU_ID);

  public readonly settingsUrl = ['/', APP_ROUTES.App, APP_ROUTES.Settings];

  public readonly appVersion = environment.appVersion;

  public readonly vm$ = {
    user: this.store.select(CurrentUserSelectors.selectUser),
  };

  close() {
    this.menuCtrl.close(this.userMenuId);
  }

  logOut() {
    this.store.dispatch(SessionActions.eventReceived({ event: { type: 'signOutRequested' } }));
    this.close();
  }

  launchIntercom() {
    this.store.dispatch(NavMenuIntercomActions.showIntercom());
    this.close();
  }

  copyEmail(email?: string) {
    if (!email) return;
    Clipboard.write({ string: email });
  }
}
